import aa from 'search-insights';

import {
  getAuthenticatedUserToken,
  getUserTokenForAlgolia,
} from '@/lib/algoliaUserToken';
import { userState } from '@/states/user';

import { ALGOLIA_EVENT_NAME } from './const';
import type { AlgoliaEvent } from './types';
import { AlgoliaEventsNames } from './types';

/**
 * Send an Algolia analytics event.
 *
 * Per Algolia docs:
 * - Always send `userToken` (anonymous, stable across login)
 * - When authenticated, also send `authenticatedUserToken` (`user_<userId>`)
 *
 * Callers can override tokens in the event payload.
 */
export const sendAlgoliaEvent = (algoliaEvent: AlgoliaEvent) => {
  // Auto-generate tokens based on user state
  const isAuthenticated = userState.isAuthenticated;
  const userId = userState.user?.id;

  const userToken = algoliaEvent.userToken ?? getUserTokenForAlgolia();
  const authenticatedUserToken =
    algoliaEvent.authenticatedUserToken ??
    getAuthenticatedUserToken(isAuthenticated, userId);

  // Always include userToken; add authenticatedUserToken when available
  const baseParams = authenticatedUserToken
    ? { userToken, authenticatedUserToken }
    : { userToken };

  sendEventWithParams(algoliaEvent, baseParams);
};

/**
 * Internal helper to send event with given token params
 */
const sendEventWithParams = (
  algoliaEvent: AlgoliaEvent,
  baseParams: { userToken?: string; authenticatedUserToken?: string },
) => {
  switch (algoliaEvent.event) {
    case AlgoliaEventsNames.clickedObjectIDsAfterSearch:
      aa(algoliaEvent.event, {
        ...baseParams,
        index: algoliaEvent.indexName ?? '',
        eventName: ALGOLIA_EVENT_NAME.productClicked,
        objectIDs: algoliaEvent.objectIDs ?? [],
        queryID: algoliaEvent.queryID ?? '',
        positions: algoliaEvent.positions ?? [],
      });
      break;
    case AlgoliaEventsNames.convertedObjectIDsAfterSearch:
      aa(algoliaEvent.event, {
        ...baseParams,
        index: algoliaEvent.indexName ?? '',
        eventName: ALGOLIA_EVENT_NAME.productConverted,
        objectIDs: algoliaEvent.objectIDs ?? [],
        queryID: algoliaEvent.queryID ?? '',
      });
      break;
    case AlgoliaEventsNames.addedToCartObjectIDsAfterSearch:
      aa(algoliaEvent.event, {
        ...baseParams,
        index: algoliaEvent.indexName ?? '',
        eventName: ALGOLIA_EVENT_NAME.productAddedToCart,
        objectIDs: algoliaEvent.objectIDs ?? [],
        objectData: algoliaEvent.objectData,
        queryID: algoliaEvent.queryID ?? '',
        value: algoliaEvent.value,
        currency: algoliaEvent.currency,
      });
      break;
    case AlgoliaEventsNames.purchasedObjectIDsAfterSearch:
      aa(algoliaEvent.event, {
        ...baseParams,
        index: algoliaEvent.indexName ?? '',
        eventName: ALGOLIA_EVENT_NAME.productPurchased,
        objectIDs: algoliaEvent.objectIDs ?? [],
        objectData: algoliaEvent.objectData,
        queryID: algoliaEvent.queryID ?? '',
        value: algoliaEvent.value,
        currency: algoliaEvent.currency,
      });
      break;
    case AlgoliaEventsNames.clickedObjectIDs:
      aa(algoliaEvent.event, {
        ...baseParams,
        index: algoliaEvent.indexName ?? '',
        eventName: ALGOLIA_EVENT_NAME.productClicked,
        objectIDs: algoliaEvent.objectIDs ?? [],
      });
      break;
    // inferQueryID makes search-insights fill each objectData queryID from the
    // queryID it cached when the product was clicked after a search.
    case AlgoliaEventsNames.addedToCartObjectIDs:
      aa(
        algoliaEvent.event,
        {
          ...baseParams,
          index: algoliaEvent.indexName ?? '',
          eventName: ALGOLIA_EVENT_NAME.productAddedToCart,
          objectIDs: algoliaEvent.objectIDs ?? [],
          objectData: algoliaEvent.objectData,
          value: algoliaEvent.value,
          currency: algoliaEvent.currency,
        },
        { inferQueryID: true },
      );
      break;
    case AlgoliaEventsNames.purchasedObjectIDs:
      aa(
        algoliaEvent.event,
        {
          ...baseParams,
          index: algoliaEvent.indexName ?? '',
          eventName: ALGOLIA_EVENT_NAME.productPurchased,
          objectIDs: algoliaEvent.objectIDs ?? [],
          objectData: algoliaEvent.objectData,
          value: algoliaEvent.value,
          currency: algoliaEvent.currency,
        },
        { inferQueryID: true },
      );
      break;
  }
};
